import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../services/api';
import { API_BASE_URL } from '../config';
import { VD, GRAD, RADIO } from '../lib/brand';
import { VondiLogo } from './ui/VondiLogo';

/*
  Muestrario de precios reales — `PANTALLA_MUESTRARIO_PRENDAS_HOME`.

  «Lo que cuesta sin vondi y con vondi.» Es el argumento más concreto que tiene
  el producto: no un porcentaje abstracto, sino una prenda con su foto, el
  precio de tienda tachado y lo que costaría con la membresía.

  Dos cosas del mockup que son deliberadas y conviene no perder al tocarlo:

    · **La tarjeta es de vondi, no de la tienda.** Va siempre en el degradado de
      marca aunque la prenda sea de Adolfo Domínguez, con el nombre del aliado
      en pequeño arriba y el wordmark blanco al lado. Lo que se está vendiendo
      aquí es el ahorro, no la marca del aliado.
    · **El precio con vondi es lo más grande de la pantalla**, encima del
      tachado y del ahorro. El orden de lectura es: cuánto costaba → cuánto
      cuesta → cuánto te quedas.

  **Vive en su propia pestaña de la zona de miembros** (Iván, 12-ago-2026), no
  embutido en la primera pantalla. El mockup ya lo insinuaba: es una pantalla
  con su titular a cuatro líneas y las tarjetas debajo, no una sección más
  dentro del catálogo de beneficios.

  Si no hay prendas cargadas se dice **por qué está vacío** en vez de dejar la
  pestaña en blanco: al ser una pestaña propia, el usuario llegó aquí a
  propósito y merece una explicación, no una pantalla muda.
*/

const ANCHO = 268;
const HUECO = 14;
const PASO = ANCHO + HUECO;

const pesos = (n) => `$${Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;

function Prenda({ item }) {
  const imagen = item.image
    ? (item.image.startsWith('http') ? item.image : `${API_BASE_URL}${item.image}`)
    : null;

  return (
    <article
      style={{
        width: ANCHO,
        flex: `0 0 ${ANCHO}px`,
        scrollSnapAlign: 'center',
        background: GRAD.marca,
        borderRadius: 28,
        padding: '18px 18px 18px',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 22px 44px -20px rgba(247,62,72,0.65)',
      }}
    >
      {/*
        Aliado + marca.

        Si la prenda está enlazada a una tienda del catálogo, se usa **su logo**
        —la misma imagen que las flip cards ponen en su zona C— y si no, el
        nombre en texto, como en el mockup. La altura va acotada a propósito:
        el manual pide PNG blanco transparente, pero hoy varias tiendas tienen
        fotos como logo, y con un tope la foto queda como un sello discreto en
        vez de comerse la cabecera.
      */}
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        {item.store_logo ? (
          <img
            src={item.store_logo}
            alt={item.store_name}
            style={{ height: 22, maxWidth: 130, objectFit: 'contain', flex: 'none' }}
          />
        ) : (
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: '#fff',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {item.store_name}
          </span>
        )}
        {/* Sobre el degradado, el wordmark va en blanco (manual §5). */}
        <VondiLogo variant="blanco" size={13} />
      </header>

      {/* La prenda. Los PNG que sube Iván vienen recortados, así que no llevan
          fondo ni recorte: se dejan respirar sobre el degradado. */}
      <div
        style={{
          height: 176,
          margin: '14px 0 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {imagen ? (
          <img
            src={imagen}
            alt={item.product_name || item.store_name}
            style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
          />
        ) : (
          <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>
            {item.product_name || 'Prenda'}
          </span>
        )}
      </div>

      {/* El bloque de precios, en el vidrio del manual sobre color (§3). */}
      <div
        style={{
          borderRadius: RADIO.card,
          padding: '14px 16px 16px',
          background: 'rgba(255,255,255,0.16)',
          border: '1.5px solid rgba(255,255,255,0.34)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
        }}
      >
        <div
          style={{
            fontSize: 13.5,
            color: 'rgba(255,255,255,0.85)',
            textDecoration: 'line-through',
            lineHeight: 1.2,
          }}
        >
          Precio tienda {pesos(item.price_regular)}
        </div>

        <div
          className="font-display"
          style={{
            fontSize: 34,
            fontWeight: 700,
            letterSpacing: '-0.03em',
            color: '#fff',
            lineHeight: 1.1,
            margin: '2px 0 4px',
          }}
        >
          {pesos(item.price_vondi)}
        </div>

        <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>
          Ahorras {pesos(item.savings ?? item.price_regular - item.price_vondi)}
        </div>
      </div>
    </article>
  );
}

/*
  El encabezado llega por props porque lo edita el admin desde el editor de la
  landing (Iván, 30-ago-2026: «el texto debe ser Ejemplo de productos y precios
  reales»). El subtítulo llega vacío por omisión: el título nuevo ya dice
  «precios reales» y repetirlo debajo sobraba.
*/
export default function MuestrarioPrecios({
  enLanding = false,
  titulo = 'Ejemplo de productos y precios reales',
  subtitulo = '',
}) {
  const [items, setItems] = useState(null);
  const pistaRef = useRef(null);
  const [activo, setActivo] = useState(0);

  useEffect(() => {
    let vigente = true;
    apiClient
      .get(`${API_BASE_URL}/api/price-showcase`)
      .then((r) => vigente && setItems(r?.data ?? []))
      .catch(() => vigente && setItems([]));
    return () => {
      vigente = false;
    };
  }, []);

  const alDesplazar = useCallback(() => {
    const el = pistaRef.current;
    if (!el) return;
    setActivo(Math.round(el.scrollLeft / PASO));
  }, []);

  const irA = (i) => {
    pistaRef.current?.scrollTo({ left: i * PASO, behavior: 'smooth' });
  };

  if (!items) return null;

  if (items.length === 0) {
    return (
      <section style={{ padding: enLanding ? '8px 24px 24px' : 'max(62px, env(safe-area-inset-top, 62px)) 24px 120px' }}>
        <h2
          className="font-display"
          style={{
            fontSize: 'clamp(30px, 9vw, 40px)', fontWeight: 700,
            letterSpacing: '-0.035em', lineHeight: 1.02, color: VD.tinta, margin: 0, maxWidth: 320,
          }}
        >
          {titulo}
        </h2>
        <p style={{ fontSize: 15, color: VD.pizarra, margin: '16px 0 0', lineHeight: 1.5 }}>
          Estamos preparando los precios reales de las prendas. Vuelve en unos días.
        </p>
      </section>
    );
  }

  return (
    <section style={{ padding: enLanding ? '8px 0 8px' : 'max(62px, env(safe-area-inset-top, 62px)) 0 120px' }}>
      <div style={{ padding: '0 24px' }}>
        <h2
          className="font-display"
          style={{
            fontSize: 'clamp(34px, 10vw, 46px)',
            fontWeight: 700,
            letterSpacing: '-0.035em',
            lineHeight: 1.02,
            color: VD.tinta,
            margin: 0,
            maxWidth: 320,
          }}
        >
          {titulo}
        </h2>
        {subtitulo ? (
          <p
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: VD.pizarra,
              margin: '10px 0 0',
              paddingLeft: 10,
            }}
          >
            {subtitulo}
          </p>
        ) : null}
      </div>

      <div
        ref={pistaRef}
        onScroll={alDesplazar}
        className="mp-no-scrollbar"
        style={{
          display: 'flex',
          gap: HUECO,
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          /* El asomo del mockup: se ve el borde de la tarjeta siguiente, que es
             lo que invita a deslizar sin necesidad de una flecha. */
          padding: '26px 24px 10px',
          scrollbarWidth: 'none',
        }}
      >
        {items.map((item) => (
          <Prenda key={item.id} item={item} />
        ))}
      </div>

      {items.length > 1 && (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 4 }}>
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => irA(i)}
              aria-label={`Ver prenda ${i + 1}`}
              style={{
                width: i === activo ? 22 : 6,
                height: 6,
                borderRadius: RADIO.pill,
                background: i === activo ? VD.tinta : VD.linea,
                transition: 'all .25s ease',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
}
